/* eslint-disable no-restricted-globals */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "services/api";
import dayjs from "dayjs";
import { ArrowUp, ChevronDown, ChevronUp, Loader2, PrinterIcon, SendIcon } from "lucide-react";
import 'dayjs/locale/pt-br' // carregar sob demanda
import { Button } from "components/Button";
import { useTranslation } from "react-i18next";
import { Report, ReportType } from "models/report";
import { useGroupStore } from "stores/useGroup";
import { Group } from "models/group";

dayjs.locale('pt-br')

interface Subtitle {
  id: string; // Tempo inicial em segundos
  start: number; // Tempo inicial em segundos
  end: number; // Tempo final em segundos
  text: string;
}

interface PatientListItemProps {
  patient: any;
}
function PatientListItem({ patient }: PatientListItemProps) {
  const [opened, setOpened] = useState(false);
  return (
    <>
      <strong className="cursor-pointer flex items-center gap-2" onClick={_ => setOpened(!opened)}>
        {patient?.name}
        {opened 
          ?<ChevronUp className="text-zinc-400" size={16} />
          :<ChevronDown className="text-zinc-400" size={16} />
        }
      </strong>
      {opened && <ul>
        <li><strong>Dias de vida</strong>: {patient?.daysLife}</li>
        <li><strong>Sobre</strong>: {patient?.summary}</li>
        <li><strong>Ações</strong>: {patient?.actions}</li>
      </ul>}
    </>
  )
}

interface ChatGPTAskBoxProps {
  report: Report;
  group: Group;
}
function ChatGPTAskBox({report, group}: ChatGPTAskBoxProps) {
  const [fetching, setFetching] = useState(false);
  const [response, setResponse] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  function sendQuestion() {
    setFetching(true);
    setResponse(null);
    api.post(`/groups/${group.id}/reports/${report.id}/ask`, {
      question: textRef?.current?.value
    }).then(res => {
      setResponse(res.data);
      setFetching(false);
    });
  }
  return (
    <>
        <div className="flex gap-2 items-center">
          <textarea ref={textRef} placeholder="Mensagem ao ChatGPT" className="bg-white border border-zinc-200 p-4 rounded-md w-full" />
          <Button onClick={sendQuestion} disabled={fetching} className="h-14 w-14">
            {fetching ? <Loader2 className="animate-spin" size={20} /> : <ArrowUp size={20} />}
          </Button>
        </div>
        {response && <div className="rounded-md bg-white border border-zinc-200 p-4">
          <h3 className="m-0">Resposta do ChatGPT</h3>
          <p>
            {response}
          </p>
        </div>}
    </>
  )
}

export function ReportPage() {
  const { reportId } = useParams();
  const group = useGroupStore(state => state.group);
  const [report, setReport] = useState<Report | null>(null);
  const [json, setJson] = useState<any | null>(null);
  const [pending, setPending] = useState(false);
  const { t } = useTranslation()

  const [subtitles, setSubtitles] = useState<Subtitle[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Função para converter tempo VTT para segundos
  const timeToSeconds = (time: string): number => {
    const [hours, minutes, seconds] = time.split(':');
    return (
      parseInt(hours) * 3600 +
      parseInt(minutes) * 60 +
      parseFloat(seconds.replace(',', '.'))
    );
  };

  function downloadReportPDF() {

  }

  function reload() {
    if (reportId) {
      api.get(`/groups/${group?.id}/reports/${reportId}`).then(async (res) => {
        setReport(res.data);
        if (res.data.type === ReportType.visitorPass) { //or others
          api.get(`/groups/${group?.id}/reports/file/${res.data.guid}.json`).then(async (resJson) => {
            setJson(resJson.data);
          });
        }
        api.get(`/groups/${group?.id}/reports/file/${res.data.guid}.vtt`).then(async (resVtt) => {
          const lines = resVtt.data.split('\n\n');

          const parsedSubtitles: Subtitle[] = lines
            .map((line: string) => {
              const [id, time, text] = line.split('\n');
              if (!text || !time.includes("-->")) return null;

              const [start, end] = time?.split(' --> ');

              return {
                id,
                start: timeToSeconds(start),
                end: timeToSeconds(end),
                text,
              };
            })
            .filter((subtitle: Subtitle) => subtitle !== null); // Filtra objetos válidos
          setSubtitles(parsedSubtitles);
        });
      });
    }
  }
  useEffect(reload, []);

  // Função para avançar o vídeo para o tempo selecionado
  const seekToTime = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      videoRef.current.play();
    }
  };

  if (report === null) {
    return (
      <main className="prose w-full mx-auto max-w-5xl">
        <div className="flex justify-between">
          <div>
            <div className="h-6 w-96 rounded bg-zinc-300 animate-pulse" />
            <div className="mt-2 h-6 w-32 rounded bg-zinc-300 animate-pulse" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="prose w-full mx-auto max-w-5xl">
      <div className="flex justify-between mb-10">
        <div>
          <h2 className="m-0">{t("reportPage.title")}</h2>
          <p className="m-0">{t("reportPage.description")}</p>
        </div>
        <div className="flex items-center">
          <Button disabled={true} onClick={downloadReportPDF}>
            {pending ? <Loader2 size={16} className="animate-spin" /> : <PrinterIcon size={16} />}
            {t("reportPage.print")}
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex gap-4 h-96 w-full bg-black rounded-md overflow-hidden">
          <video ref={videoRef} controls className="h-full m-0 w-full" crossOrigin="anonymous">
            <source src={`${api.defaults.baseURL}/groups/${group?.id}/reports/file/${report.guid}.mp4`} type="video/mp4" />
            <track src={`${api.defaults.baseURL}/groups/${group?.id}/reports/file/${report.guid}.vtt`} kind="subtitles" srcLang="pt" label="Português" default />
            Seu navegador não suporta a tag de vídeo.
          </video>
          <ul className="h-full overflow-y-auto w-1/2 m-0">
            {subtitles.map((subtitle, index) => (
              <li className="cursor-pointer text-white text-xs" key={index} onClick={() => seekToTime(subtitle.start)}>
                <strong className="text-zinc-500">{new Date(subtitle.start * 1000).toISOString().substr(11, 8)}</strong>: {subtitle.text}
              </li>
            ))}
          </ul>
        </div>
        {
          report.type === ReportType.visitorPass && json && <div className="flex flex-col gap-2">
            <div className="rounded-md bg-white border border-zinc-200 p-4">
              <h3 className="m-0">Resumo</h3>
              <p>
                {json?.summary}
              </p>
            </div>
            <div className="flex gap-2">
              <div className="rounded-md bg-white border border-zinc-200 p-4 flex-1">
                <h3 className="m-0">Pacientes</h3>
                <ul>
                  {json?.patients?.map((patient: any) => (
                    <li>
                      <PatientListItem patient={patient} />
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-md bg-white border border-zinc-200 p-4 flex-1">
                <h3 className="m-0">Ações</h3>
                <ul>
                  {json?.actions?.map((action: string) => (
                    <li>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        }
        <ChatGPTAskBox group={group!} report={report} />
      </div>
    </main>
  );
}
